import React, { useEffect, useState } from "react";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import {
  Typography,
  TextField,
  Button,
  Box,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Container,
  useTheme,
  CircularProgress,
} from "@mui/material";
import CommonModal from "../modal/CommonModal";
import { tokens } from "../../../theme";
import { AdminService } from "../../../services/dataService";
import { isEqual } from "lodash";

// Validation helpers
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidPhone = (phone) => {
  const phoneRegex = /^\d{10}$/;
  return phoneRegex.test(phone);
};

const EditItem = ({ fields, onSave, onCancel, editData }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [editedItem, setEditedItem] = useState(editData || {});
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [errors, setErrors] = useState({});

  const getLabel = (label, isRequired) => (
    <>
      {label}
      {isRequired && (
        <span style={{ color: "red", marginLeft: 4 }}>*</span>
      )}
    </>
  );
  const requiredLabel = (text) => (
    <>
      {text} <span style={{ color: "red" }}> *</span>
    </>
  );

  // Check if RM details should be shown based on valid_days
  const shouldShowRmDetails = () => {
    const validDaysExcluded = [1, 2, 9, 13];
    const selectedValidDays = parseInt(editedItem.valid_days);
    return !validDaysExcluded.includes(selectedValidDays) && editedItem.valid_days;
  };

  useEffect(() => {
    if (editData) {
      setEditedItem(editData);
    }
  }, [editData]);

  const handleClose = () => {
    onCancel();
  };

  const handleFieldChange = (name, value) => {
    setEditedItem((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Validate RM fields for format
  const validateRmFields = () => {
    const newErrors = {};
    let isValid = true;

    if (shouldShowRmDetails()) {
      if (!editedItem.rm_name || editedItem.rm_name.trim() === "") {
        newErrors.rm_name = "RM Name is required";
        isValid = false;
      }

      if (!editedItem.rm_mobile || editedItem.rm_mobile.trim() === "") {
        newErrors.rm_mobile = "Mobile No is required";
        isValid = false;
      } else if (!isValidPhone(editedItem.rm_mobile.trim())) {
        newErrors.rm_mobile = "Phone number must be exactly 10 digits";
        isValid = false;
      }

      if (!editedItem.rm_email || editedItem.rm_email.trim() === "") {
        newErrors.rm_email = "Email is required";
        isValid = false;
      } else if (!isValidEmail(editedItem.rm_email.trim())) {
        newErrors.rm_email = "Please enter a valid email address";
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  // Check if all required fields are filled
  const areAllRequiredFieldsFilled = () => {
    const optionalFields = ["institute_domain", "university_name"];

    const baseFieldsValid = fields.every((field) => {
      const value = editedItem[field.name];

      // skip optional
      if (optionalFields.includes(field.name)) return true;

      // REQUIRED SWITCH VALIDATION
      if (field.type === "switch") {
        if (field.required) {
          return value !== undefined && value !== null;
        }
        return true;
      }

      // normal fields
      return value !== undefined && value !== null && value !== "";
    });

    if (!baseFieldsValid) return false;

    // Validate RM fields with format checks
    if (!validateRmFields()) return false;

    return true;
  };

  const handleSave = () => {
    if (areAllRequiredFieldsFilled()) {
      setIsConfirmationModalOpen(true);
    }
  };

  const handleConfirmSave = () => {
    onSave(editedItem);
    setIsConfirmationModalOpen(false);
  };

  const handleCloseModal = () => {
    setIsConfirmationModalOpen(false);
  };

  return (
    <div style={{ position: "relative", height: "100%" }}>
      <Box sx={{ overflowY: "auto", pb: 12 }}>
        <Grid container spacing={2}>
          {fields.map((field) => (
            <Grid item xs={12} xl={6} key={field.name}>
              {field.type === "switch" ? (
                <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        name={field.name}
                        color={field.name === "reg_open" ? "success" : "info"}
                        sx={{ ml: 1.5 }}
                        checked={
                          field.name === "status"
                            ? editedItem.status === "Active"
                            : field.name === "reg_open"
                            ? editedItem.reg_open === 1
                            : false
                        }
                        onChange={(e) => {
                          if (field.name === "status") {
                            handleFieldChange(
                              "status",
                              e.target.checked ? "Active" : "InActive"
                            );
                          } else if (field.name === "reg_open") {
                            handleFieldChange("reg_open", e.target.checked ? 1 : 0);
                          }
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {field.name === "status"
                          ? editedItem.status === "Active"
                            ? "Active"
                            : "InActive"
                          : field.name === "reg_open"
                          ? editedItem.reg_open === 1
                            ? "Open"
                            : "Closed"
                          : ""}
                        {field.required && (
                          <span style={{ color: "red", marginLeft: 4 }}>*</span>
                        )}
                      </Typography>
                    }
                  />
                </Box>
              ) : field.type === "date" ? (
                <TextField
                  label={getLabel(field.label, field.required)}
                  required={false}
                  color="info"
                  size="small"
                  name={field.name}
                  type="date"
                  value={editedItem[field.name] || "null"}
                  onChange={(e) =>
                    handleFieldChange(field.name, e.target.value)
                  }
                  fullWidth
                  margin="dense"
                />
              ) : (
                <TextField
                  label={getLabel(field.label, field.required)}
                  required={false}
                  color="info"
                  size="small"
                  name={field.name}
                  type={field.type === "number" ? "number" : ""}
                  value={editedItem[field.name] || ""}
                  onChange={(e) =>
                    handleFieldChange(field.name, e.target.value)
                  }
                  fullWidth
                  margin="dense"
                  select={field.type === "select"}
                >
                  {field.type === "select" &&
                    field.options.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                </TextField>
              )}
            </Grid>
          ))}
          {/* Conditional RM Details Section */}
          {shouldShowRmDetails() && (
            <Grid item xs={12}>
              <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.primary[300]}` }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Relationship Manager Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} xl={6}>
                    <TextField
                      label={requiredLabel("Name")}
                      color="info"
                      size="small"
                      name="rm_name"
                      value={editedItem.rm_name || ""}
                      onChange={(e) =>
                        handleFieldChange("rm_name", e.target.value)
                      }
                      fullWidth
                      margin="dense"
                      error={!!errors.rm_name}
                      helperText={errors.rm_name || ""}
                    />
                  </Grid>
                  <Grid item xs={12} xl={6}>
                    <TextField
                      label={requiredLabel("Mobile No")}
                      color="info"
                      size="small"
                      name="rm_mobile"
                      type="text"
                      value={editedItem.rm_mobile || ""}
                      onChange={(e) => {
                        // Only allow digits, max 10
                        const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                        handleFieldChange("rm_mobile", val);
                      }}
                      fullWidth
                      margin="dense"
                      error={!!errors.rm_mobile}
                      helperText={errors.rm_mobile || ""}
                      inputProps={{ maxLength: 10 }}
                    />
                  </Grid>
                  <Grid item xs={12} xl={6}>
                    <TextField
                      label={requiredLabel("Email")}
                      color="info"
                      size="small"
                      name="rm_email"
                      type="text"
                      autoComplete="off"
                      value={editedItem.rm_email || ""}
                      onChange={(e) =>
                        handleFieldChange("rm_email", e.target.value)
                      }
                      onFocus={(e) => e.target.select()}
                      fullWidth
                      margin="dense"
                      error={!!errors.rm_email}
                      helperText={errors.rm_email || ""}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>

      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
          p: 1,
          bgcolor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
          zIndex: 1,
        }}
      >
        <Button
          variant="contained"
          sx={{ mr: 1 }}
          color="info"
          onClick={handleSave}
        >
          Update
        </Button>
        <Button variant="contained" color="inherit" onClick={handleClose}>
          Cancel
        </Button>
      </Box>
      <CommonModal
        open={isConfirmationModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmSave}
        action="update"
      />
    </div>
  );
};

const CommonDrawer = ({
  isOpen,
  onClose,
  fields,
  title,
  setSelectedEditItems,
  memNo,
  institueId,
  isEditDrawerOpen,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleClose = () => {
    setEditData(null);
    onClose();
  };

  useEffect(() => {
    const fetchData = async (institueId) => {
      setLoading(true);
      try {
        const response = await AdminService.admin_institute(institueId);
        const data = response.data.data;
        const rawData = data[0];

        // Also fetch RM details
        let rmData = null;
        try {
          const rmResponse = await AdminService.admin_rm_details(institueId);
          rmData = rmResponse.data?.data;
        } catch (rmErr) {
          console.error("RM details fetch error:", rmErr);
        }

        // Map backend field names to component field names
        const mappedData = {
          ...rawData,
          mem_no: memNo,
          // Convert status from numeric (1/0) to string ("Active"/"InActive")
          status: rawData.status === 1 || rawData.status === "1" || rawData.status === "Active" ? "Active" : "InActive",
          // Convert reg_open to number if it's a string
          reg_open: typeof rawData.reg_open === "string" ? parseInt(rawData.reg_open, 10) : rawData.reg_open,
          // Ensure state_name is set for the select field (it maps to state_id in API)
          state_name: rawData.state_id ? rawData.state_id.toString() : rawData.state_name || "",
          // Ensure country_id is set
          country_id: rawData.country_id ? rawData.country_id : null,
          // Map RM details - prefer data from RM API, fallback to institute data
          rm_email: rmData?.rm_email || rmData?.email || rawData.rm_email || "",
          rm_mobile: rmData?.rm_mobile || rmData?.mobile_no || rawData.rm_mobile || "",
          rm_name: rmData?.rm_name || rawData.rm_name || "",
        };

        setEditData(mappedData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (institueId) {
      fetchData(institueId);
    }
  }, [institueId, isEditDrawerOpen, memNo]);

  return (
    <Drawer anchor="left" open={isOpen}>
      {/* <div style={{ maxWidth: "600px", padding: "16px" }}> */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: colors.blueAccent[800],
          px: 2,
          py: 0.5,
        }}
      >
        <Typography
          variant="h6"
          color={colors.blueAccent[200]}
          fontWeight={600}
        >
          {/* {title} */}
          {title || ""}
        </Typography>
        <IconButton
          color="inherit"
          aria-label="Close Drawer"
          onClick={handleClose}
          edge="end"
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Container
        sx={{
          [theme.breakpoints.down("sm")]: {
            maxWidth: "600px",
          },
          [theme.breakpoints.up("sm")]: {
            width: "600px",
          },
          my: 1,
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "90vh",
            }}
          >
            <CircularProgress color="info" />
          </Box> // Display a loader while fetching data
        ) : editData ? (
          <EditItem
            title={title}
            fields={fields}
            editData={editData}
            onSave={(editedItem) => {
              setSelectedEditItems(editedItem);
            }}
            onCancel={handleClose}
          />
        ) : (
          <Typography
            variant="subtitle1"
            style={{ color: colors.redAccent[400] }}
          >
            Sorry, no data found.
          </Typography>
        )}
        {/* </div> */}
      </Container>
    </Drawer>
  );
};

export default CommonDrawer;
